import { JSX, useContext, useEffect, useMemo } from "react";
import { PageSection, SearchBox } from "@app/shared";
import { QESAlert, QESRadioButton } from "@app/shared/qes-standards";
import { CreateExceptionsContext } from "../contexts/CreateExceptionsContext";
import { WizardStepProps } from "../contexts/interfaces";

const CreateExceptionsSpecialties = (props: WizardStepProps): JSX.Element => {
  const wizardContext = useContext(CreateExceptionsContext);

  useEffect(() => {
    props.pageIsValid(!wizardContext.loading && !!wizardContext.selectedSpecialty);
  }, [wizardContext.selectedSpecialty]);

  const specialties = useMemo(() => {
    return wizardContext.specialties.filter((specialty) => specialty.isVisible).map((specialty) => (
      <li key={specialty.displayName}>
        <QESRadioButton
          label={specialty.displayName}
          value={specialty.displayName}
          checked={wizardContext.selectedSpecialty === `${specialty.displayName}:${specialty.specialtyGroupCode}`}
          testId={`create-exceptions-wizard__specialty-radiobutton--${specialty.displayName}`}
          onClick={(e) => { e.stopPropagation(); }}
          onChange={() => wizardContext.updateSelectedSpecialty(specialty.displayName, specialty.specialtyGroupCode)}
          onLabelClick={(e) => { e.stopPropagation(); }}
        />
      </li>
    ));
  }, [wizardContext]);

  const showNoSpecialtiesAlert = useMemo(() => {
    const hasServiceAreas = wizardContext.selectedServiceArea.length === 1;
    return hasServiceAreas && specialties.length === 0 && !wizardContext.loading;
  }, [wizardContext.serviceAreas, specialties, wizardContext.loading]);

  const updateSearchString = (value: string) => {
    wizardContext.updateSpecialtiesVisibility(value);
  }

  return (
    <PageSection>
      <div className="container mt-3 p-0">
        {showNoSpecialtiesAlert &&
          <QESAlert type="warning" persist={true}>
            The service areas you selected do not have any specialty groups or projects in common.
          </QESAlert>
        }

        {wizardContext.showChangeWarning && (
          <QESAlert type="warning" persist={true} classNames="mt-3">Changing anything on this page will reset later steps.</QESAlert>
        )}

        {wizardContext.error && (
          <QESAlert type="danger" persist={true} classNames="mt-3">
            {wizardContext.error}
          </QESAlert>
        )}
      </div>

      <div className="container mt-3">
        <SearchBox
          escapeSearchValue={true}
          fieldsetClass="row"
          inputLabel="Search Specialties"
          testId="create-exceptions-search-specialties"
          onSubmit={(value: string) => updateSearchString(value)}
        />
      </div>

      <div className="container">
        <div className="row">
          <div className="w-100">
            <div className="border">
              <ul className="vr__list vr__list--specialty">
                {specialties}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default CreateExceptionsSpecialties;
